'use client'

/**
 * Layout Context
 *
 * This file implements a React Context to manage the global layout state of the application,
 * specifically handling the sidebar open/closed state.
 */

import { createContext, useContext, useReducer, useCallback, type Dispatch, type ReactNode } from 'react'

/**
 * Interface defining the shape of the layout state
 */
interface LayoutState {
  isSidebarOpen: boolean
}

/**
 * Type defining the possible actions that can be dispatched to update layout state
 */
type LayoutAction = { type: 'TOGGLE_SIDEBAR' } | { type: 'SET_SIDEBAR', payload: boolean }

/**
 * Initial state for the layout context
 */
const initialState: LayoutState = {
  isSidebarOpen: false
}

/**
 * Reducer function to handle layout state updates based on dispatched actions
 */
const layoutReducer = (state: LayoutState, action: LayoutAction): LayoutState => {
  switch (action.type) {
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        isSidebarOpen: !state.isSidebarOpen
      }
    case 'SET_SIDEBAR':
      return {
        ...state,
        isSidebarOpen: action.payload
      }
    default:
      return state
  }
}

/**
 * Context to store the current layout state
 */
const LayoutStateContext = createContext<LayoutState | undefined>(undefined)

/**
 * Context to store the dispatch function for updating layout state
 */
const LayoutDispatchContext = createContext<Dispatch<LayoutAction> | undefined>(undefined)

/**
 * Provider component that wraps the app to provide layout state management
 */
export const LayoutContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(layoutReducer, initialState)

  return (
    <LayoutStateContext.Provider value={state}>
      <LayoutDispatchContext.Provider value={dispatch}>
        {children}
      </LayoutDispatchContext.Provider>
    </LayoutStateContext.Provider>
  )
}

/**
 * Hook to access the current state
 */
export const useLayoutState = () => {
  const state = useContext(LayoutStateContext)
  const dispatch = useContext(LayoutDispatchContext)

  if (state === undefined || dispatch === undefined) {
    throw new Error('useLayoutState must be used within a LayoutContextProvider')
  }

  const toggleSidebar = useCallback(() => {
    if (dispatch) {
      dispatch({ type: 'TOGGLE_SIDEBAR' })
    }
  }, [dispatch])

  const setSidebar = useCallback((isOpen: boolean) => {
    if (dispatch) {
      dispatch({ type: 'SET_SIDEBAR', payload: isOpen })
    }
  }, [dispatch])

  return {
    sidebarOpen: state.isSidebarOpen,
    toggleSidebar,
    setSidebar
  }
}

export default LayoutContextProvider
